// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useInboundSummary } from '@hooks/other/useInboundSummary';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, Box, IconButton, Popover, Typography } from '@mui/material';
import { NotificationRow } from './NotificationRow';

export const Notifications: React.FC = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const nav = useNavigate();
  const { counts, totalInbound } = useInboundSummary();
  const goAllocations = () => {
    nav('/registry/allocationRequest');
  };
  const goMints = () => {
    nav('/registry/mints');
  };
  const goBurns = () => {
    nav('/registry/burns');
  };
  const goTransfers = () => {
    nav('/registry/transfers');
  };
  const goCredentials = () => {
    nav('/credential/offers');
  };
  const goCredentialOnboarding = () => {
    nav('/credential/onboarding');
  };
  const goCsa = () => {
    nav('/settings/commercial-agreements');
  };
  const notifications: { label: string; onClick: () => void; count: number }[] = [
    {
      label: 'Allocation requests',
      onClick: goAllocations,
      count: counts.allocations,
    },
    {
      label: 'Mint requests',
      onClick: goMints,
      count: counts.mints,
    },
    {
      label: 'Transfer requests',
      onClick: goTransfers,
      count: counts.transfers,
    },
    {
      label: 'Credential offers',
      onClick: goCredentials,
      count: counts.credentials,
    },
    {
      label: 'Burn requests',
      onClick: goBurns,
      count: counts.burns,
    },
    {
      label: 'registrar requests',
      onClick: goCredentialOnboarding,
      count: counts.registrars,
    },
    {
      label: 'provider requests',
      onClick: goCredentialOnboarding,
      count: counts.providers,
    },
    {
      label: 'holder requests',
      onClick: goCredentialOnboarding,
      count: counts.holders,
    },
    {
      label: 'Commercial service agreement requests',
      onClick: goCsa,
      count: counts.csa,
    },
  ];
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;
  return (
    <>
      <IconButton
        aria-label="notifications"
        size="small"
        sx={{ display: 'flex', alignItems: 'center' }}
        onClick={handleClick}
      >
        <Badge
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          showZero
          variant="dot"
          color="error"
          invisible={totalInbound === 0}
        >
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: '300px' }}>
          {totalInbound === 0 && (
            <Typography variant="body2" sx={{ textAlign: 'center', p: 0 }}>
              No notifications
            </Typography>
          )}
          {notifications.map((n) => (
            <NotificationRow
              key={n.label}
              label={n.label}
              onClick={() => {
                n.onClick();
                handleClose();
              }}
              count={n.count}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};
